import { format, parse } from "date-fns"
import { AxiosError } from "axios"
import { v4 as uuidv4 } from "uuid"

/**
 * Capitalizes the first letter of a string
 * @param string - The string to capitalize
 * @returns string - The string with the first letter capitalized
 */
const capitalizeFirstLetter = (string: string) => {
	return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Removes the API version prefix from a URL
 * @param url - The URL to clean
 * @returns string - The URL without the "/api/v1/" prefix
 */
const removeApiVersion = (url: string): string => {
	return url.replace("/api/v1/", "/")
}

/**
 * Copies a string to the system clipboard
 * @param str - The string to copy to clipboard
 */
const copyToClipboard = (str: string) => {
	navigator.clipboard.writeText(str)
}

/**
 * Type guard to check if an error is an Axios error
 * @param error - The error to check
 * @returns boolean - True if the error is an Axios error, false otherwise
 */
const isAxiosError = (error: unknown): error is AxiosError => {
	return (error as AxiosError).isAxiosError !== undefined
}

/**
 * Formats a time string from "HH:MM:SS" format to a more readable format
 * @param time - Time string in "HH:MM:SS" format
 * @returns string - Formatted time string (e.g., "2h 30m 45s")
 */
const formatTime = (time: string) => {
	if (time) {
		const [hours, minutes, seconds] = time.split(":").map(Number)

		const formatedHours = hours > 0 ? `${hours}h` : ""
		const formatedMinutes = minutes > 0 ? `${minutes}m` : ""
		const formatedSeconds = `${seconds}s`

		return `${formatedHours} ${formatedMinutes} ${formatedSeconds}`
	}
}

/**
 * Formats estimated time in seconds to a human-readable format
 * @param estimatedSeconds - The estimated time in seconds
 * @returns string - Formatted time string (e.g., "5 min 30 sec left")
 */
const formatEstimateTime = (estimatedSeconds: number) => {
	if (estimatedSeconds) {
		const minutes = Math.floor(estimatedSeconds / 60)
		const seconds = Math.floor(estimatedSeconds % 60)
		const formatedEstimate = `${minutes} min ${seconds} sec left`
		return formatedEstimate
	}
}

/**
 * Converts a date string from one format to another
 * @param date - The date string to format
 * @param inputFormat - The format of the input date string (e.g., "yyyy-MM-dd")
 * @param outputFormat - The desired output format (e.g., "dd MMM yyyy")
 * @returns string - The formatted date string
 */
const formatDate = (date: string, inputFormat: string, outputFormat: string) => {
	if (date === "") return date
	try {
		const cleanedDate = date.trim()
		const parsedDate = parse(cleanedDate, inputFormat, new Date())

		return format(parsedDate, outputFormat)
	} catch (e) {
		console.error("Error formatting date:", e)
		return String(date)
	}
}

/**
 * Converts an object type code to a human-readable text for tooltips
 * @param type - The object type identifier
 * @returns string - Human-readable description of the object type
 */
const objectTypeTextTooltip = (type: string) => {
	switch (type) {
		case "left_part_of_dual_object":
			return "Left part"

		case "right_part_of_dual_object":
			return "Right part"

		case "whole_dual_object":
			return "Full object"

		case "shelves_object":
			return "Shelves object"

		case "composite_object":
			return "Composite object"

		case "column_based_object":
			return "Column based object"
	}
}

/**
 * Generates a NATS subject with pattern ai_chat.uuid4
 * @returns string - The generated NATS subject
 */
const generateNatsSubject = () => {
	return `ai_chat.${uuidv4()}`
}

/**
 * Checks if there are any ongoing processes in custom chat or predefined questions
 * @param data - Object containing chat and questions status information
 * @returns boolean - True if any process is ongoing, false otherwise
 */
const hasOngoingProcesses = (
	data: { custom_chat?: { status: string }; predefined_questions?: { status: string } } | undefined,
) => {
	const isCustomChatOngoing = data?.custom_chat?.status === "ongoing"
	const isPredefinedQuestionsOngoing = data?.predefined_questions?.status === "ongoing"
	return isCustomChatOngoing || isPredefinedQuestionsOngoing
}

/**
 * Parses a file size string and returns the numeric value in megabytes
 * @param sizeString - The file size string to parse (e.g., "123.45MB", "500MB")
 * @returns number - The numeric value in megabytes, or 0 if the string is invalid or undefined
 */
const parseFileSizeInMB = (sizeString?: string): number => {
	if (!sizeString) return 0
	const match = sizeString.match(/^(\d+(?:\.\d+)?)MB$/i)
	return match ? parseFloat(match[1]) : 0
}

/**
 * Validates that a value is a positive integer
 * @param fieldName - The name of the field being validated
 * @returns A validator function for Ant Design Form
 */
const integerValidator = (fieldName: string) => (_: unknown, value: string | number) => {
	if (Number(value) < 0) {
		return Promise.reject(new Error("Please enter a positive number"))
	}
	if (!Number.isInteger(Number(value))) {
		return Promise.reject(new Error("Please enter a whole number"))
	}
	if (Number(value) > 3) {
		return Promise.reject(new Error(`Please enter a ${fieldName} lower than 4`))
	}
	return Promise.resolve()
}

/**
 * Checks if a value is a valid positive integer
 * @param value - The value to check
 * @returns boolean - True if the value is a positive integer, false otherwise
 */
const isPositiveInteger = (value: string | number | null | undefined): boolean => {
	if (value === undefined || value === null || value === "" || String(value).trim() === "" || Number(value) > 3) {
		return false
	}
	return Number.isInteger(Number(value)) && Number(value) >= 0
}

/**
 * Converts a base64 data URL to a File object
 * @param dataUrl - The base64 data URL string (e.g., "data:image/png;base64,...")
 * @param fileName - The name to assign to the resulting File
 * @returns File - A File object with the decoded binary data and detected MIME type
 * @throws Error if the data URL is malformed or missing required parts
 */
const convertDataUrlToFile = (dataUrl: string, fileName: string) => {
	const [meta, content] = dataUrl.split(",")
	if (!meta || !content) throw new Error("Invalid image payload")

	const mimeMatch = meta.match(/data:(.*?);base64/)
	const mimeType = mimeMatch?.[1] || "image/png"
	const binaryString = atob(content)
	const bytes = new Uint8Array(binaryString.length)

	for (let index = 0; index < binaryString.length; index += 1) {
		bytes[index] = binaryString.charCodeAt(index)
	}

	return new File([bytes], fileName, { type: mimeType })
}

export {
	capitalizeFirstLetter,
	removeApiVersion,
	copyToClipboard,
	isAxiosError,
	formatTime,
	objectTypeTextTooltip,
	formatDate,
	formatEstimateTime,
	generateNatsSubject,
	hasOngoingProcesses,
	parseFileSizeInMB,
	integerValidator,
	isPositiveInteger,
	convertDataUrlToFile,
}
